/**
 * Feature registry — single source of truth for what each gateable feature requires.
 *
 * Every feature gated by <PremiumGate feature="..."> or useCanUse("...") MUST
 * have an entry here. This keeps gating decisions reviewable in one place and
 * prevents scattered `if (isPremium)` drift.
 *
 * `requires` vocabulary (handled by satisfies() in resolvePremium.js):
 *   - 'premium'             — any form of premium (individual or school-paid)
 *   - 'school.pro'          — user is on a school-paid Pro seat
 *   - 'school.basic_or_pro' — either paid school tier
 *   - 'ai_seat'             — AI capability (individual teacher_pro includes it;
 *                             school seat must have aiSeat=true)
 *   - 'teacher.pro'         — individual Teacher Pro OR school Pro
 *   - 'parent.pro'          — individual Parent Pro
 */

export const FEATURES = {
  // ── Layer 2 — institution (school-paid only) ──
  emailParents:        { requires: 'school.pro',      label: 'Email parents' },
  smsParents:          { requires: 'school.pro',      label: 'SMS parents' },
  liveDashboards:      { requires: 'school.pro',      label: 'Live cross-staff dashboards' },
  workflows:           { requires: 'school.pro',      label: 'Workflows' },
  customBranding:      { requires: 'school.pro',      label: 'Custom branding' },
  aiMessageTagging:    { requires: 'school.pro',      label: 'AI message tagging' },

  // ── AI seat (layered on Pro, or individual Teacher Pro) ──
  aiAssistant:         { requires: 'ai_seat',         label: 'AI assistant' },

  // ── Layer 1 capacity — individual OR school-pro covers it ──
  unlimitedStudents:   { requires: 'premium',         label: 'Unlimited students' },
  unlimitedClasses:    { requires: 'premium',         label: 'Unlimited classes' },
  unlimitedSmartLists: { requires: 'premium',         label: 'Unlimited smart lists' },
  unlimitedSeating:    { requires: 'premium',         label: 'Unlimited seating plans' },
  fullQueueHistory:    { requires: 'premium',         label: 'Full queue history' },
  lessonNotes:         { requires: 'teacher.pro',     label: 'Lesson notes & planning' },

  // ── Parent Pro ──
  initiateMessaging:   { requires: 'parent.pro',      label: 'Initiate messaging with teachers' },
  multiChildDashboard: { requires: 'parent.pro',      label: 'Unified multi-child dashboard' },
  allTimeHistory:      { requires: 'parent.pro',      label: 'All-time history' },
  parentTrends:        { requires: 'parent.pro',      label: 'Trends & patterns' },
  parentInsights:      { requires: 'parent.pro',      label: 'Early warning insights' },
  parentRules:         { requires: 'parent.pro',      label: 'Custom notification rules' },
  calendarSync:        { requires: 'parent.pro',      label: 'Calendar sync' },
};

export function getFeature(key) {
  return FEATURES[key] || null;
}
